import { useMemo } from 'react';

/**
 * Custom hook for Resource Planning calculations
 * Centralizes all KPI and utilization calculations for consistency
 */
export const useResourceCalculations = ({
  dateRange,
  timelineData,
  planningSettings,
  filterEmployee,
  filterProject
}) => {
  
  // Calculate timeline days from date range
  const timelineDays = useMemo(() => {
    const days = [];
    const start = new Date(dateRange.start + 'T00:00:00');
    const end = new Date(dateRange.end + 'T00:00:00');
    
    let current = new Date(start);
    while (current <= end) {
      // Use local date formatting to avoid timezone issues
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      days.push({
        date: dateStr,
        dayOfWeek: current.getDay(),
        dayOfMonth: current.getDate(),
        month: current.getMonth(),
        isWeekend: current.getDay() === 0 || current.getDay() === 6,
        isToday: current.toDateString() === new Date().toDateString(),
        isFirstOfMonth: current.getDate() === 1
      });
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [dateRange]);

  // Get months for header display
  const months = useMemo(() => {
    const monthsMap = {};
    timelineDays.forEach(day => {
      const monthKey = `${day.month}-${new Date(day.date).getFullYear()}`;
      if (!monthsMap[monthKey]) {
        monthsMap[monthKey] = {
          name: new Date(day.date).toLocaleDateString('de-CH', { month: 'long', year: 'numeric' }),
          days: 0,
          startIndex: timelineDays.findIndex(d => d.date === day.date && d.isFirstOfMonth)
        };
      }
      monthsMap[monthKey].days++;
    });
    return Object.values(monthsMap);
  }, [timelineDays]);

  // Calculate filtered hours KPIs based on current date range
  const filteredHoursKPI = useMemo(() => {
    if (!timelineData?.employees || !planningSettings) {
      return {
        totalProjectHours: 0,
        totalAdminHours: 0,
        totalAvailableHours: 0,
        totalPlannedHours: 0,
        utilizationPercent: 0,
        workingDays: 0,
        absenceDays: 0,
        absenceHours: 0,
        overbookedHours: 0,
        employeeCount: 0,
        totalAvailableProjectHours: 0
      };
    }

    const { project_work_percent } = planningSettings;
    const adminPercent = 100 - project_work_percent;
    const workingDays = timelineDays.filter(day => !day.isWeekend).length;

    // Filter employees based on filters
    let relevantEmployees = timelineData.employees;
    if (filterEmployee) {
      relevantEmployees = relevantEmployees.filter(emp => emp.user_id === filterEmployee);
    }
    if (filterProject) {
      relevantEmployees = relevantEmployees.filter(emp => {
        const projectLanes = emp.project_lanes || [];
        return projectLanes.some(lane => lane.project_id === filterProject);
      });
    }

    let totalProjectHours = 0;
    let totalAdminHours = 0;
    let totalOverbookedHours = 0;
    let totalAbsenceHours = 0;

    relevantEmployees.forEach(employee => {
      const employeeWeeklyHours = employee.weekly_hours || 42.5;
      const employeeDailyHours = employeeWeeklyHours / 5;
      const employeeProjectHoursPerDay = employeeDailyHours * (project_work_percent / 100);
      const employeeAdminHoursPerDay = employeeDailyHours * (adminPercent / 100);

      // Build absence dates set
      const absenceDates = new Set();
      (employee.absences || []).forEach(absence => {
        const absStart = new Date(absence.start_date);
        const absEnd = new Date(absence.end_date);
        absStart.setHours(0, 0, 0, 0);
        absEnd.setHours(0, 0, 0, 0);
        
        timelineDays.forEach(day => {
          if (day.isWeekend) return;
          
          const currentDay = new Date(day.date);
          currentDay.setHours(0, 0, 0, 0);
          
          if (currentDay >= absStart && currentDay <= absEnd) {
            absenceDates.add(day.date);
            const isHalfDay = (currentDay.getTime() === absStart.getTime() && absence.half_day_start) ||
                              (currentDay.getTime() === absEnd.getTime() && absence.half_day_end);
            totalAbsenceHours += isHalfDay ? employeeDailyHours / 2 : employeeDailyHours;
          }
        });
      });

      // Calculate project hours from tasks
      const projectLanes = employee.project_lanes || [];
      const filteredLanes = filterProject 
        ? projectLanes.filter(lane => lane.project_id === filterProject)
        : projectLanes;

      const dayAllocations = new Map();

      filteredLanes.forEach(lane => {
        lane.tasks.forEach(task => {
          // Create normalized dates ONCE outside the inner loop
          const taskStart = new Date(task.start_date);
          taskStart.setHours(0, 0, 0, 0);
          const taskEnd = new Date(task.end_date);
          taskEnd.setHours(0, 0, 0, 0);
          
          timelineDays.forEach(day => {
            if (day.isWeekend) return;
            if (absenceDates.has(day.date)) return;
            
            const currentDay = new Date(day.date);
            currentDay.setHours(0, 0, 0, 0);
            
            if (currentDay >= taskStart && currentDay <= taskEnd) {
              const allocationPercent = task.allocation_percent || 100;
              const taskHours = employeeProjectHoursPerDay * (allocationPercent / 100);
              totalProjectHours += taskHours;
              
              const currentAlloc = dayAllocations.get(day.date) || 0;
              dayAllocations.set(day.date, currentAlloc + allocationPercent);
            }
          });
        });
      });

      // Calculate admin hours and overbooking
      dayAllocations.forEach((totalAlloc) => {
        const effectiveAlloc = Math.min(totalAlloc, 100);
        totalAdminHours += employeeAdminHoursPerDay * (effectiveAlloc / 100);
        
        if (totalAlloc > 100) {
          const overbookedPercent = totalAlloc - 100;
          totalOverbookedHours += employeeProjectHoursPerDay * (overbookedPercent / 100);
        }
      });
    });

    // Calculate total available project hours
    let totalAvailableProjectHours = 0;
    relevantEmployees.forEach(employee => {
      const employeeWeeklyHours = employee.weekly_hours || 42.5;
      const employeeDailyHours = employeeWeeklyHours / 5;
      const employeeProjectHoursPerDay = employeeDailyHours * (project_work_percent / 100);
      
      const absenceDates = new Set();
      (employee.absences || []).forEach(absence => {
        const absStart = new Date(absence.start_date);
        const absEnd = new Date(absence.end_date);
        absStart.setHours(0, 0, 0, 0);
        absEnd.setHours(0, 0, 0, 0);
        
        timelineDays.forEach(day => {
          if (day.isWeekend) return;
          const currentDay = new Date(day.date);
          currentDay.setHours(0, 0, 0, 0);
          if (currentDay >= absStart && currentDay <= absEnd) {
            absenceDates.add(day.date);
          }
        });
      });
      
      const availableDays = timelineDays.filter(day => !day.isWeekend && !absenceDates.has(day.date)).length;
      totalAvailableProjectHours += availableDays * employeeProjectHoursPerDay;
    });

    const utilizationPercent = totalAvailableProjectHours > 0 
      ? Math.round((totalProjectHours / totalAvailableProjectHours) * 100) 
      : 0;

    return {
      totalProjectHours: Math.round(totalProjectHours * 10) / 10,
      totalAdminHours: Math.round(totalAdminHours * 10) / 10,
      totalAvailableProjectHours: Math.round(totalAvailableProjectHours * 10) / 10,
      totalPlannedHours: Math.round((totalProjectHours + totalAdminHours) * 10) / 10,
      utilizationPercent,
      workingDays,
      absenceHours: Math.round(totalAbsenceHours * 10) / 10,
      employeeCount: relevantEmployees.length,
      overbookedHours: Math.round(totalOverbookedHours * 10) / 10
    };
  }, [timelineData, timelineDays, planningSettings, filterEmployee, filterProject]);

  // Calculate detailed utilization per employee
  const calculatedEmployeeUtilization = useMemo(() => {
    if (!timelineData?.employees || !planningSettings) {
      return [];
    }

    const { project_work_percent } = planningSettings;
    const workingDays = timelineDays.filter(day => !day.isWeekend).length;

    let relevantEmployees = timelineData.employees;
    if (filterEmployee) {
      relevantEmployees = relevantEmployees.filter(emp => emp.user_id === filterEmployee);
    }
    if (filterProject) {
      relevantEmployees = relevantEmployees.filter(emp => {
        const projectLanes = emp.project_lanes || [];
        return projectLanes.some(lane => lane.project_id === filterProject);
      });
    }

    return relevantEmployees.map(employee => {
      const employeeWeeklyHours = employee.weekly_hours || 42.5;
      const employeeDailyHours = employeeWeeklyHours / 5;
      const employeeProjectHoursPerDay = employeeDailyHours * (project_work_percent / 100);

      // Build absence dates set
      const absenceDates = new Set();
      let absenceHours = 0;
      (employee.absences || []).forEach(absence => {
        const absStart = new Date(absence.start_date);
        const absEnd = new Date(absence.end_date);
        absStart.setHours(0, 0, 0, 0);
        absEnd.setHours(0, 0, 0, 0);
        
        timelineDays.forEach(day => {
          if (day.isWeekend) return;
          const currentDay = new Date(day.date);
          currentDay.setHours(0, 0, 0, 0);
          
          if (currentDay >= absStart && currentDay <= absEnd) {
            absenceDates.add(day.date);
            const isHalfDay = (currentDay.getTime() === absStart.getTime() && absence.half_day_start) ||
                              (currentDay.getTime() === absEnd.getTime() && absence.half_day_end);
            absenceHours += isHalfDay ? employeeDailyHours / 2 : employeeDailyHours;
          }
        });
      });

      const totalCapacityHours = workingDays * employeeDailyHours;
      const effectiveCapacityHours = totalCapacityHours - absenceHours;
      const availableDays = timelineDays.filter(day => !day.isWeekend && !absenceDates.has(day.date)).length;
      const totalAvailableProjectHours = availableDays * employeeProjectHoursPerDay;

      // Calculate planned hours from tasks
      let totalPlannedProjectHours = 0;
      const projectLanes = employee.project_lanes || [];
      const filteredLanes = filterProject 
        ? projectLanes.filter(lane => lane.project_id === filterProject)
        : projectLanes;

      filteredLanes.forEach(lane => {
        lane.tasks.forEach(task => {
          // Create normalized dates ONCE outside the inner loop
          const taskStart = new Date(task.start_date);
          taskStart.setHours(0, 0, 0, 0);
          const taskEnd = new Date(task.end_date);
          taskEnd.setHours(0, 0, 0, 0);
          
          timelineDays.forEach(day => {
            if (day.isWeekend) return;
            if (absenceDates.has(day.date)) return;
            
            const currentDay = new Date(day.date);
            currentDay.setHours(0, 0, 0, 0);
            
            if (currentDay >= taskStart && currentDay <= taskEnd) {
              const allocationPercent = task.allocation_percent || 100;
              const taskHours = employeeProjectHoursPerDay * (allocationPercent / 100);
              totalPlannedProjectHours += taskHours;
            }
          });
        });
      });

      const utilizationPercent = totalAvailableProjectHours > 0 
        ? Math.round((totalPlannedProjectHours / totalAvailableProjectHours) * 100) 
        : 0;

      // Determine status
      let status = 'low';
      let statusLabel = 'Gering';
      if (utilizationPercent > 100) {
        status = 'overbooked';
        statusLabel = 'Überbucht';
      } else if (utilizationPercent >= 80) {
        status = 'high';
        statusLabel = 'Hoch';
      } else if (utilizationPercent >= 50) {
        status = 'normal';
        statusLabel = 'Normal';
      }

      return {
        user_id: employee.user_id,
        user_name: employee.user_name,
        weekly_hours: employeeWeeklyHours,
        daily_hours: Math.round(employeeDailyHours * 10) / 10,
        working_days: workingDays,
        total_capacity_hours: Math.round(totalCapacityHours * 10) / 10,
        absence_hours: Math.round(absenceHours * 10) / 10,
        effective_capacity_hours: Math.round(effectiveCapacityHours * 10) / 10,
        total_available_project_hours: Math.round(totalAvailableProjectHours * 10) / 10,
        allocated_hours: Math.round(totalPlannedProjectHours * 10) / 10,
        available_hours: Math.round(Math.max(0, totalAvailableProjectHours - totalPlannedProjectHours) * 10) / 10,
        utilization_percent: utilizationPercent,
        status,
        status_label: statusLabel
      };
    });
  }, [timelineData, timelineDays, planningSettings, filterEmployee, filterProject]);

  // Summary KPIs derived from employee utilization
  const filteredSummaryKPI = useMemo(() => {
    if (!calculatedEmployeeUtilization || calculatedEmployeeUtilization.length === 0) {
      return { total: 0, overbooked: 0, high: 0, normal: 0, low: 0 };
    }

    return {
      total: calculatedEmployeeUtilization.length,
      overbooked: calculatedEmployeeUtilization.filter(e => e.status === 'overbooked').length,
      high: calculatedEmployeeUtilization.filter(e => e.status === 'high').length,
      normal: calculatedEmployeeUtilization.filter(e => e.status === 'normal').length,
      low: calculatedEmployeeUtilization.filter(e => e.status === 'low').length
    };
  }, [calculatedEmployeeUtilization]);

  return {
    timelineDays,
    months,
    filteredHoursKPI,
    calculatedEmployeeUtilization,
    filteredSummaryKPI
  };
};

export default useResourceCalculations;
